import { Group, Select as MantineSelect, TextInput } from '@mantine/core';
import debounce from 'lodash/debounce';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFeatureFlag } from '../../../../helpers/feature-flags/feature-flags.hooks';
import { FeatureFlag } from '../../../../helpers/feature-flags/feature-flags.type';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { useStrategyStore } from '../../../../store/strategy-store/strategy-store';
import {
  setActivePanel,
  setSearchTerm,
  setSelectedInitiative,
  setTableGroupBy,
  setTreeMapMetric,
  setView,
} from '../../../../store/strategy-store/strategy-store.actions';
import { Panel, TableGroupBy, TreeMapMetric, View } from '../../../../store/strategy-store/strategy-store.type';
import { baseWhite, inkLight } from '../../../../styles/design-tokens';
import { Button } from '../../../../ui-library/button/button';
import { Icon } from '../../../../ui-library/icon/icon';
import { Select } from '../../../../ui-library/select/select';
import { Text } from '../../../../ui-library/typography/typography';
import { useFilteredInitiatives } from '../../strategy.hooks';
import { PresentationModal } from '../presentation-modal/presentation-modal';
import { MainSectionExportActions } from './main-section-export-actions';
import { getTreeMapMetricOptions, handleDownloadCSV } from './main-section.helpers';

export const MainSectionControls = () => {
  const { t } = useTranslation();
  const teams = useGlobalStore((state) => state.teams);
  const organization = useGlobalStore((state) => state.organization);
  const activeView = useStrategyStore((state) => state.view);
  const searchTerm = useStrategyStore((state) => state.searchTerm);
  const tableGroupBy = useStrategyStore((state) => state.tableGroupBy);
  const treeMapMetric = useStrategyStore((state) => state.treeMapMetric);
  const kpiOverviewData = useStrategyStore((state) => state.kpiOverviewData);
  const showParentOutcomes = useFeatureFlag(FeatureFlag.CorporateGoals);
  const { initiatives } = useFilteredInitiatives();

  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const [presentationModalOpen, setPresentationModalOpen] = useState(false);

  const groupByOptions = useMemo(() => {
    const options = [
      { value: TableGroupBy.Team, label: t('common.team') },
      { value: TableGroupBy.Status, label: t('strategy.filters.status') },
      { value: TableGroupBy.Source, label: t('strategy.filters.source') },
    ];

    if (showParentOutcomes) {
      options.unshift({
        value: TableGroupBy.Outcome,
        label: t('strategy.filters.parent_outcomes'),
      });
    }

    return options;
  }, [showParentOutcomes, t]);

  const getButtonVariant = (view: View) => {
    return activeView === view ? 'secondary' : 'outline';
  };

  const getIconColor = (view: View) => {
    return activeView === view ? baseWhite : inkLight;
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchTerm(value);
      }, 200),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  const handleViewChange = (view: View) => {
    setView(view);
    setActivePanel(Panel.Filters);
    setSelectedInitiative(null);
  };

  const disableControls = initiatives.length === 0;

  return (
    <div>
      <Group justify="space-between" miw={760} wrap="nowrap">
        <Group gap={16} wrap="nowrap">
          <Group gap={8} wrap="nowrap">
            <Button
              variant={getButtonVariant(View.Table)}
              radius="xl"
              size="sm"
              leftSection={
                <Icon
                  name="format_list_bulleted"
                  color={getIconColor(View.Table)}
                  size={16}
                  style={{ cursor: 'pointer' }}
                />
              }
              onClick={() => handleViewChange(View.Table)}
            >
              {t('strategy.initiatives.table_view')}
            </Button>
            <Button
              variant={getButtonVariant(View.TreeMap)}
              radius="xl"
              size="sm"
              leftSection={
                <Icon name="border_all" color={getIconColor(View.TreeMap)} size={16} style={{ cursor: 'pointer' }} />
              }
              onClick={() => handleViewChange(View.TreeMap)}
            >
              {t('strategy.initiatives.tree_map_view')}
            </Button>
          </Group>
          {activeView === View.Table && !disableControls && (
            <Group gap={8} wrap="nowrap">
              <Text size="small" lineHeight="none" style={{ whiteSpace: 'nowrap' }}>
                {t('strategy.initiatives.group_by')}
              </Text>
              <MantineSelect
                data={groupByOptions}
                value={tableGroupBy}
                onChange={(value) => {
                  setTableGroupBy(value as TableGroupBy);
                }}
                clearable
                radius="xl"
                size="sm"
                w={150}
                placeholder={t('common.placeholders.select_a_group')}
              />
            </Group>
          )}
          {activeView === View.TreeMap && !disableControls && (
            <Group gap={8} wrap="nowrap">
              <Text size="small" lineHeight="none">
                {t('strategy.initiatives.metric')}
              </Text>
              <Select
                value={treeMapMetric}
                options={getTreeMapMetricOptions(kpiOverviewData, t)}
                onChange={(value) => {
                  setTreeMapMetric(value as TreeMapMetric);
                }}
                style={{ width: 240 }}
              />
            </Group>
          )}
        </Group>
        <Group wrap="nowrap">
          {activeView === View.Table && (
            <TextInput
              placeholder={t('strategy.initiatives.search_placeholder')}
              radius="xl"
              size="sm"
              value={localSearchTerm}
              leftSection={<Icon name="search" variant="filled" size={16} color={inkLight} />}
              style={{ width: 300 }}
              onChange={(event) => {
                const value = event.currentTarget.value;
                setLocalSearchTerm(value);
                debouncedSearch(value);
              }}
            />
          )}
          <MainSectionExportActions
            disabled={disableControls}
            onExportCSV={() => handleDownloadCSV(initiatives, teams, t)}
            onCreatePresentation={() => setPresentationModalOpen(true)}
            canExportPresentations={!!organization?.can_export_presentations}
          />
        </Group>
      </Group>

      {organization?.can_export_presentations && (
        <PresentationModal
          opened={presentationModalOpen}
          onClose={() => setPresentationModalOpen(false)}
          initiativeIds={initiatives.map((initiative) => initiative.id)}
          initiativesCount={initiatives.length}
        />
      )}
    </div>
  );
};
